import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfileStore } from '@/store'
import type {
	DDSListOutput,
	DDSMeListOutput,
	DDSMeListParams,
	DDSOutput,
	DDSUpdateInput,
} from '@/types'
import {
	createDDS,
	exportDDSPdf,
	getDDSById,
	getDDSList,
	getDDSMeList,
	updateDDS,
} from '../services/dds'

const QUERY_KEY = ['dds']
const ME_QUERY_KEY = ['dds', 'me']

export function useDDSList(params?: { page?: number; limit?: number }) {
	return useQuery<DDSListOutput, Error>({
		queryKey: [...QUERY_KEY, params],
		queryFn: () => getDDSList(params),
		staleTime: 10 * 60 * 1000, // 10 minutos
	})
}

export function useDDSMeList(params?: DDSMeListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: DDSMeListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<DDSMeListOutput, Error>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getDDSMeList(scopedParams),
		staleTime: 10 * 60 * 1000,
	})
}

export function useDDSById(id: string) {
	return useQuery<DDSOutput, Error>({
		queryKey: ['dds', id],
		queryFn: () => getDDSById(id),
		staleTime: 10 * 60 * 1000, // 10 minutos
	})
}

export function useCreateDDS() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: createDDS,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
		},
	})
}

export function useUpdateDDS() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: DDSUpdateInput }) =>
			updateDDS(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
		},
	})
}

export function useExportDDSPdf() {
	return useMutation<Blob, Error, string>({
		mutationFn: exportDDSPdf,
	})
}
